// ServiceBookingFlowStore.ts
import { makeObservable, observable, action } from 'mobx';

// Define steps in the booking flow
export enum BookingStep {
  USER_SELECTION = 1,
  EXISTING_PATIENT = 2,
  TIME_SELECTION = 3,
  LOGIN = 4,
  FAMILY_MEMBER = 5,
  ADDRESS_MEDICARE = 6,
  CONFIRMATION = 7,
  SERVICE_LOGIN = 8,
}

// Define the interface for appointment data
interface AppointmentData {
  isForUser: boolean | null;
  isExistingPatient: boolean | null;
  serviceType: any | null;
  appointmentType: any | null;
  selectedDoctor: any | null;
  selectedTime: any | null;
  selectedFamilyMember: any | null;
}

class ServiceBookingFlowStore {
  // UI State
  isModalOpen = false;
  isFromLoginStep = false;
  isServiceBooking = false; // Add missing property
  configurationDetails: any = null;
  currentStep = BookingStep.TIME_SELECTION;
  activeStep = 1;
  isBookingConfirmed = false;
  showFamilyMemberStep = false;
  skipServiceTypeSelection = false; // Flag to determine if service type selection should be skipped
  previousStep: number | null = null;
  // Data
  appointmentData: AppointmentData = {
    isForUser: null,
    isExistingPatient: null,
    serviceType: null,
    appointmentType: null,
    selectedDoctor: null,
    selectedTime: null,
    selectedFamilyMember: null,
  };

  appointmentDetails: any = null;

  appointmentTypes: any = null;

  constructor() {
    makeObservable(this, {
      isModalOpen: observable,
      isFromLoginStep: observable,
      isServiceBooking: observable,
      configurationDetails: observable,
      currentStep: observable,
      activeStep: observable,
      isBookingConfirmed: observable,
      showFamilyMemberStep: observable,
      skipServiceTypeSelection: observable,
      previousStep: observable,
      appointmentData: observable,
      appointmentDetails: observable,
      appointmentTypes: observable,
      setIsModalOpen: action,
      setActiveStep: action,
      updateAppointmentData: action,
      resetFlow: action,
      nextStep: action,
      prevStep: action,
      goToStep: action,
      goToLogin: action,
      handleBackClick: action,
      handleServiceTypeSelection: action,
      handleAppointmentTypeSelection: action,
      handleFamilyMemberSelection: action,
      setAppointmentDetails: action,
      setAppointmentTypes: action,
      setBookingConfirmed: action,
      setShowFamilyMemberStep: action,
      setSkipServiceTypeSelection: action,
      getSkipServiceTypeSelection: action,
      setServiceBooking: action, // Added missing action
      setConfiguration: action, // Added missing action
      getSelectedDoctor: action // Added missing action
    });
  }

  // Modal state management
  setIsModalOpen = (isOpen: boolean) => {
    this.isModalOpen = isOpen;
    // Reset to initial step if closing the modal
    if (!isOpen) {
      this.currentStep = BookingStep.USER_SELECTION;
      this.activeStep = 0;
    }
  };

  // Navigation methods
  setActiveStep = (step: number) => {
    this.activeStep = step;
  };

  nextStep = () => {
    // If current step is service type and we should skip it, go to appointment type
    // console.log('Current Step:', this.currentStep);
    // console.log('Skip Service Type Selection:', this.skipServiceTypeSelection);

    this.currentStep += 1;


    // Update active step to match current step
    this.activeStep = this.currentStep;
  };

  prevStep = () => {

    this.currentStep -= 1;
    // Update active step to match current step
    this.activeStep = this.currentStep;
  };

  goToStep = (step: BookingStep) => {
    this.currentStep = step;
  };

  goToLogin = () => {
    this.goToStep(BookingStep.LOGIN);
  };

  goToTime= () => {
    this.goToStep(BookingStep.TIME_SELECTION);
  };

  goToServiceLogin = () => {
    this.goToStep(BookingStep.SERVICE_LOGIN);
  };

  goToAddress = () => {
    this.goToStep(BookingStep.ADDRESS_MEDICARE);
  };

  goToConformation = () => {
    this.goToStep(BookingStep.CONFIRMATION);
  };

  comefromLogin = () => {
    this.isFromLoginStep = true;
  };

  handleBackClick = () => {
    // Handle back navigation for User Selection step
    if (this.currentStep === BookingStep.USER_SELECTION) {
      if (this.showFamilyMemberStep) {
        this.setShowFamilyMemberStep(false);
        this.goToStep(BookingStep.USER_SELECTION);
      } else {
        this.isModalOpen = false;
      }
      return;
    }

    // Handle back navigation for Family Member step
    if (this.currentStep === BookingStep.FAMILY_MEMBER) {
      this.goToStep(BookingStep.USER_SELECTION);
      return;
    }

    // Handle back navigation for Login step
    if (this.currentStep === BookingStep.LOGIN) {
      // If came from Time Selection, go back there
      if (this.previousStep === BookingStep.TIME_SELECTION) {
        this.goToStep(BookingStep.TIME_SELECTION);
      } else {
        // Default fallback to User Selection
        this.goToStep(BookingStep.USER_SELECTION);
      }
      return;
    }

    // Handle back navigation for Confirmation step
    if (this.currentStep === BookingStep.CONFIRMATION) {
      if (this.showFamilyMemberStep) {
        this.goToStep(BookingStep.FAMILY_MEMBER);
      } else {
        this.goToStep(BookingStep.TIME_SELECTION);
      }
      return;
    }

    // Handle back navigation for Existing Patient step
    if (this.currentStep === BookingStep.EXISTING_PATIENT) {
      this.goToStep(BookingStep.USER_SELECTION);
      return;
    }

    // Handle back navigation for Time Selection step
    if (this.currentStep === BookingStep.TIME_SELECTION) {
      if (this.appointmentData.isExistingPatient !== null) {
        this.goToStep(BookingStep.EXISTING_PATIENT);
      } else {
        this.goToStep(BookingStep.USER_SELECTION);
      }
      return;
    }

    // Default fallback - go to previous step
    this.prevStep();
    this.activeStep = this.currentStep;
  };

  // Data update methods
  updateAppointmentData = (key: keyof AppointmentData, value: any) => {

    this.appointmentData = {
      ...this.appointmentData,
      [key]: value,
    };
  };

  handleServiceTypeSelection = (serviceType: any) => {
    this.updateAppointmentData('serviceType', serviceType);
  };

  handleAppointmentTypeSelection = (appointmentType: any) => {
    this.updateAppointmentData('appointmentType', appointmentType);
  };

  handleFamilyMemberSelection = (familyMember: any) => {
    this.updateAppointmentData('selectedFamilyMember', familyMember);
  };

  setAppointmentDetails = (details: any) => {
    this.appointmentDetails = details;
  };

  setBookingConfirmed = (isConfirmed: boolean) => {
    this.isBookingConfirmed = isConfirmed;
  };

  setShowFamilyMemberStep = (show: boolean) => {
    this.showFamilyMemberStep = show;
  };

  setAppointmentTypes = (appointmentTypes: any) => {
    this.appointmentTypes = appointmentTypes;
  };

  // Method to set whether to skip service type selection (for when it's pre-selected from URL)
  setServiceBooking = (isServiceBooking: boolean) => {
    this.isServiceBooking = isServiceBooking;
  };

  // Method to set whether to skip service type selection (for when it's pre-selected from URL)
  setSkipServiceTypeSelection = (skip: boolean) => {
    this.skipServiceTypeSelection = skip;
  };

  setConfiguration = (config: any) => {
    this.configurationDetails = config;
  };

  // Getter for skipServiceTypeSelection flag
  getSkipServiceTypeSelection = () => {
    return this.skipServiceTypeSelection;
  };

  // Method to get the selected doctor
  getSelectedDoctor = () => {
    return this.appointmentData.selectedDoctor;
  };

  // Reset flow to initial state
  resetFlow = () => {
    this.currentStep = BookingStep.TIME_SELECTION;
    this.activeStep = 1;
    this.isBookingConfirmed = false;
    this.appointmentData = {
      isForUser: null,
      isExistingPatient: null,
      serviceType: null,
      appointmentType: null,
      selectedDoctor: null,
      selectedTime: null,
      selectedFamilyMember: null,
    };
    this.appointmentTypes = [];
    this.appointmentDetails = null;
    this.showFamilyMemberStep = false;
    this.skipServiceTypeSelection = false;
    this.isServiceBooking = true; 
    this.isFromLoginStep = false;
  }
}

// Create and export a singleton instance
export const servicebookingFlowStore = new ServiceBookingFlowStore();